/* eslint-disable @typescript-eslint/ban-ts-comment */
import { useIntl } from 'react-intl';
import { useForm, useWatch } from 'react-hook-form';
import { useState, useEffect, useMemo } from 'react';
import { TextField, Textarea, Dropdown, Button } from '@marketplace/ui-elements';
import { Icon } from '@developer/components';
import {
  TAPIHookFetchCountriesResponse,
  useAPIFetchCountries,
  useAPIFetchStates,
  useAPIFetchPartner,
  useAPIPutPartner,
  TAPIFetchPartnerResponse,
  TAPIHookFetchStatesResponse,
  TAPICountry,
  TAPIState,
  TStatesCode,
} from '@marketplace/api';
import { joiResolver } from '@hookform/resolvers/joi';

import { Uploader } from './components';
import { partnerProfileValidationSchema } from './partner-profile-form.validation';
import classes from './partner-profile-form.module.scss';
import { DropdownChangeParams } from 'primereact/dropdown';

type TPartnerProfileForm = Omit<TAPIFetchPartnerResponse['data'], 'tenants' & 'id'> & {
  country?: TAPICountry;
  state?: TAPIState;
};

export const PartnerProfileForm = () => {
  const { formatMessage } = useIntl();
  const [isEditMode, setIsEditMode] = useState(false);
  const [initialData, setInitialData] = useState<any>({});
  const [icon, setIcon] = useState<any>(null);

  const {
    data: countries,
    loading: isFetchCountriesLoading,
    fetchCountries,
  }: TAPIHookFetchCountriesResponse = useAPIFetchCountries({ silence: true });
  const { data: states, loading: isFetchStatesLoading, fetchStates }: TAPIHookFetchStatesResponse = useAPIFetchStates();
  const { fetchPartner } = useAPIFetchPartner();
  const { putPartner, loading: loadingPutPartner } = useAPIPutPartner();

  useEffect(() => {
    (async () => {
      const countryList = await fetchCountries();
      const partnerData = await fetchPartner().then((res) => res.data);

      for (const key of Object.values(TStatesCode)) {
        const currentCountryCode = countryList.find((country) => country.country === partnerData.country)?.key;

        if (currentCountryCode === key) {
          const stateList = await fetchStates(TStatesCode[key]);
          setInitialData({
            applications_description: partnerData?.applications_description,
            city: partnerData?.city,
            company_name: partnerData?.company_name,
            country: countryList?.find((item) => item?.country === partnerData?.country),
            postal_code: partnerData?.postal_code,
            state: stateList.find((item) => item?.state === partnerData?.state),
            website: partnerData?.website,
            logo: partnerData?.logo ?? '',
            address: partnerData?.address,
          });
          return;
        }
      }

        setInitialData({
          applications_description: partnerData?.applications_description,
          city: partnerData?.city,
          company_name: partnerData?.company_name,
          country: countryList?.find((item) => item?.country === partnerData?.country),
          postal_code: partnerData?.postal_code,
          state: '',
          website: partnerData?.website,
          logo: partnerData?.logo ?? '',
          address: partnerData?.address,
        });
    })();
  }, []);

  const {
    handleSubmit,
    control,
    getValues,
    setValue,
    setError,
    clearErrors,
    reset,
    trigger,
    formState: { errors, isValid, isDirty },
  } = useForm<TPartnerProfileForm>({
    resolver: joiResolver(partnerProfileValidationSchema({ formatMessage, countries })),
    mode: 'onChange',
  });
  const editModeOn = () => {
    setIsEditMode(true);
  };

  const editModeOff = () => {
    setIsEditMode(false);
  };

  const countryFieldValue = useWatch({ name: 'country', control });

  const hasStates = useMemo(() => {
    const countryFieldKey = getValues('country')?.key;

    for (const key of Object.values(TStatesCode)) {
      if (TStatesCode[key] === countryFieldKey) {
        return true;
      }
    }

    return false;
  }, [countryFieldValue]);

  const cancelEditMode = () => {
    editModeOff();
    setIcon(null);
    reset(initialData);
  };

  useEffect(() => {
    if (initialData) {
      reset(initialData);
    }
  }, [initialData]);

  const savePartnerProfile = async (data: TPartnerProfileForm) => {
    const response = await putPartner({
      ...data,
      country: data?.country?.key,
      state: data?.state?.key,
    }).then((res) => res.data);

    setInitialData({
      applications_description: response?.applications_description,
      city: response?.city,
      company_name: response?.company_name,
      country: countries?.find((item: any) => item?.country === response?.country),
      postal_code: response?.postal_code,
      state: states.find((item: any) => item?.state === response?.state),
      website: response?.website,
      logo: response?.logo ?? '',
      address: response?.address,
    });
    editModeOff();
  };

  const isDisabledSaveButton = (isValid && !isDirty) || !isValid;

  const handleCountry = (e: DropdownChangeParams) => {
    const countryFieldKey = e.value?.key;

    setValue('country', e.value, { shouldDirty: true });
    setValue('state', undefined, { shouldDirty: true });

    for (const key of Object.values(TStatesCode)) {
      if (TStatesCode[key] === countryFieldKey) {
        reset({ ...getValues(), state: '' });
        fetchStates(TStatesCode[key]);
        break;
      }
    }
  };

  return (
    <form onSubmit={handleSubmit(savePartnerProfile)} className={classes.form}>
      <button className={classes.edit_button} onClick={editModeOn} type="button">
        <Icon classes={isEditMode ? classes.editIcon_active : classes.editIcon} variant="edit-pencil" isPointer />
      </button>

      <div className={classes.first_section}>
        <Uploader
          isContentEditable={isEditMode}
          initialData={initialData}
          setInitialData={setInitialData}
          setValue={setValue}
          setError={setError}
          getValues={getValues}
          clearErrors={clearErrors}
          error={errors.logo?.message}
          icon={icon}
          setIcon={setIcon}
          trigger={trigger}
          label={formatMessage({
            id: 'developer_personal_page_info_logo',
          })}
        />
        <div className={classes.fields_section}>
          <TextField
            name="company_name"
            fieldId="company_name"
            disabled={!isEditMode}
            control={control}
            error={errors?.company_name?.message}
            maxLength={100}
            label={formatMessage({
              id: 'developer_personal_page_partner_profile_company_name',
            })}
          />
          <TextField
            name="website"
            fieldId="website"
            control={control}
            error={errors?.website?.message}
            disabled={!isEditMode}
            maxLength={2000}
            label={formatMessage({
              id: 'developer_personal_page_partner_profile_website',
            })}
          />
        </div>
      </div>
      <div className={classes.second_section}>
        <div className={classes.fields_section}>
          <Dropdown
            isRequired
            filter
            filterBy="country"
            options={countries}
            control={control}
            isLoading={isFetchCountriesLoading}
            rootClassName={classes.countryField}
            //@ts-ignore
            error={errors?.country?.message}
            disabled={!isEditMode}
            onChange={handleCountry}
            label={formatMessage({
              id: 'developer_personal_page_partner_profile_country',
            })}
            fieldId="country"
            optionLabel="country"
            name="country"
          />
          {hasStates && (
            <Dropdown
              filter
              isRequired
              options={states}
              control={control}
              optionLabel="state"
              filterBy="state"
              //@ts-ignore
              error={errors?.state?.message}
              isLoading={isFetchStatesLoading}
              disabled={!isEditMode}
              placeholder={formatMessage(
                { id: 'generalForm_placeholder_select' },
                {
                  field: formatMessage({
                    id: 'developer_personal_page_partner_profile_state',
                  }),
                }
              )}
              rootClassName={classes.stateField}
              label={formatMessage({
                id: 'developer_personal_page_partner_profile_state',
              })}
              fieldId="state"
              name="state"
            />
          )}
        </div>
        <div className={classes.fields_section}>
          <TextField
            name="city"
            fieldId="city"
            error={errors?.city?.message}
            isRequired
            control={control}
            disabled={!isEditMode}
            maxLength={100}
            label={formatMessage({
              id: 'developer_personal_page_partner_profile_city',
            })}
          />
          <TextField
            name="postal_code"
            fieldId="postal_code"
            error={errors?.postal_code?.message}
            control={control}
            maxLength={20}
            disabled={!isEditMode}
            label={formatMessage({
              id: 'developer_personal_page_partner_profile_postal_code',
            })}
          />
        </div>
      </div>
      <div>
        <TextField
          maxLength={2000}
          control={control}
          error={errors?.address?.message}
          rootClassName={classes.address}
          label={formatMessage({
            id: 'developer_personal_page_partner_profile_address',
          })}
          fieldId="address"
          name="address"
          disabled={!isEditMode}
        />
      </div>
      <div className={classes.third_section}>
        <Textarea
          name="applications_description"
          isRequired
          fieldId="applications_description"
          error={errors?.applications_description?.message}
          control={control}
          disabled={!isEditMode}
          maxLength={1000}
          label={formatMessage({
            id: 'developer_personal_page_partner_profile_description',
          })}
          rootClassName={classes.description}
        />
      </div>
      {isEditMode && (
        <div className={classes.button_wrapper}>
          <Button variant="secondary" type="button" onClick={cancelEditMode}>
            {formatMessage({
              id: 'developer_personal_page_cancel',
            })}
          </Button>
          <Button type="submit" disabled={isDisabledSaveButton} loading={loadingPutPartner}>
            {formatMessage({
              id: 'developer_personal_page_save',
            })}
          </Button>
        </div>
      )}
    </form>
  );
};
